// Solar siting model. Ported verbatim from dashboard/index.html calc()/suit()
// (L810-827). Pure and unit-tested — see solar.test.ts.
import { PROPS } from './constants';
import {
  CO2_T_PER_MWH,
  DEFAULT_YIELD_PER_KWP,
  INSTALL_COST_USD_PER_KWP,
  MODEL_VERSION,
  PANEL_AREA_M2,
  PANEL_KWP,
  TARIFF_USD_PER_KWH,
  USABLE_FRACTION,
} from './solarModel';

export type SolarProps = {
  tariff?: number;
  yieldPerKwp?: number;
};

export interface SolarMetrics {
  modelVersion: typeof MODEL_VERSION;
  usable: number;
  panels: number;
  kwp: number;
  annual: number;
  co2: number;
  revenue: number;
  capex: number;
  payback: number;
}

export function calc(area: number, props: SolarProps = PROPS): SolarMetrics {
  const tariff = Number.isFinite(props.tariff) ? (props.tariff as number) : TARIFF_USD_PER_KWH;
  // PVOUT is optional while the resource surface is being rolled out. Zero,
  // NaN, Infinity and negative values are all invalid and use the historical
  // fallback so a malformed row cannot silently produce zero energy.
  const yld = Number.isFinite(props.yieldPerKwp) && (props.yieldPerKwp as number) > 0
    ? (props.yieldPerKwp as number)
    : DEFAULT_YIELD_PER_KWP;
  const usable = area * USABLE_FRACTION;
  const panels = Math.max(0, Math.floor(usable / PANEL_AREA_M2));
  const kwp = +(panels * PANEL_KWP).toFixed(1);
  const annual = Math.round(kwp * yld);
  const co2 = +((annual * CO2_T_PER_MWH) / 1000).toFixed(1);
  const revenue = Math.round(annual * tariff);
  const capex = Math.round(kwp * INSTALL_COST_USD_PER_KWP);
  const payback = revenue > 0 ? +(capex / revenue).toFixed(1) : 0;
  return {
    modelVersion: MODEL_VERSION,
    usable: Math.round(usable),
    panels,
    kwp,
    annual,
    co2,
    revenue,
    capex,
    payback,
  };
}

export interface Suitability {
  label: 'High' | 'Medium' | 'Low';
  color: string;
  bg: string;
}

export function suit(kwp: number, conf: number): Suitability {
  if (kwp >= 250 && conf >= 85)
    return { label: 'High', color: '#3fcf8e', bg: 'rgba(63,207,142,.12)' };
  if (kwp >= 100 && conf >= 72)
    return { label: 'Medium', color: '#FAA61A', bg: 'rgba(250,166,26,.12)' };
  return { label: 'Low', color: '#e0564a', bg: 'rgba(224,86,74,.12)' };
}

// 72 = the Medium-suitability confidence floor from suit(). Below it a site is
// "Low" suitability; a missing roof makes it unactionable either way.
export const ATTENTION_CONF = 72;
